import axios, {AxiosResponse} from "axios";
import Employee from "@/model/Employee";
import {SpringPagedResult} from "@/service/PagedResult";
import {EmployeeStatus, EmploymentType, WageType, WorklogType} from "@/enums";
import EmployeeName from "@/model/EmployeeName";
import Order from "@/model/Order";
import Worklog from "@/model/Worklog";
import {parseProblemBody, toFilename} from "@/service/FileDownload";

const PATH = "/employees";
const PATH_V3 = "/v3/employees";
const PATH_MONTHLY_SUMMARIES_V3 = "/v3/employee-monthly-summaries";

/**
 * The projections the v3 employee API can return an employee as.
 */
export type EmployeeProjection = "FULL" | "SHALLOW";

/**
 * The filter criteria the v3 employee API supports. `employeeNumber` and `wage` match values starting with
 * their digits; `employmentType`, `wage` and `wageType` refer to the employment and wage valid today.
 */
export interface EmployeeV3Filter {
  term?: string | null;
  status?: EmployeeStatus | null;
  operatorNumber?: number | null;
  employeeNumber?: number | null;
  firstName?: string | null;
  lastName?: string | null;
  employmentType?: EmploymentType | null;
  wage?: number | null;
  wageType?: WageType | null;
}

/**
 * The shape every v3 employee response shares: a uuid `id` (not the numeric database id) plus the employee
 * number. `SHALLOW` responses only ever carry these fields.
 */
export interface EmployeeV3Dto {
  id: string;
  employeeNumber: number;
  firstName: string;
  lastName: string;
  status?: EmployeeStatus;
  birthdate?: string;
  email?: string;
  companyLocation?: { id: string; name: string } | null;
  firstAidCourseDate?: string | null;
  firstAidValidUntil?: string | null;
  contractExpiryDate?: string | null;
  export?: boolean;
  temporary?: boolean;
  manager?: boolean;
  teamleadSince?: string | null;
  operatorNumber?: number | null;
  inventoryBonusEligible?: boolean;
  inventoryBonusEligibleSince?: string | null;
  currentEmployment?: EmploymentPeriodV3Dto | null;
  currentWage?: WagePeriodV3Dto | null;
}

/**
 * The master data of an employee to change via the v3 employee API, as a JSON Merge Patch: a field left out keeps its
 * value, a field set to `null` is cleared. The v3 API ignores every other field, so a v1 field name such as
 * `employeeId` or `companyLocation` would be dropped without an error. An employee eligible for the inventory bonus
 * needs `inventoryBonusEligibleSince`; `inventoryBonusEligible: false` clears it.
 */
export interface EmployeeV3Patch {
  employeeNumber?: number;
  firstName?: string;
  lastName?: string;
  birthdate?: string;
  email?: string;
  companyLocationId?: string | null;
  firstAidCourseDate?: string | null;
  firstAidValidUntil?: string | null;
  contractExpiryDate?: string | null;
  export?: boolean;
  temporary?: boolean;
  manager?: boolean;
  teamleadSince?: string | null;
  operatorNumber?: number | null;
  inventoryBonusEligible?: boolean;
  inventoryBonusEligibleSince?: string | null;
}

/**
 * One wage period as the v3 employee API returns it. Unlike the v1 {@link Wage}, its `id` is a uuid and the
 * wage is called `amount`.
 */
export interface WagePeriodV3Dto {
  id: string;
  amount: number;
  wageType: WageType;
  targetHours: number | null;
  validFrom: string;
  validTo: string | null;
}

/**
 * The body to create a wage period via the v3 employee API. `validFrom` is the month the wage period starts in
 * (`YYYY-MM`); it starts on the first day of that month.
 */
export interface WagePeriodV3Request {
  amount: number;
  wageType: WageType;
  targetHours: number | null;
  validFrom: string;
}

/**
 * The body to update a wage period via the v3 employee API. The start and end date can not be changed, and a
 * `null` `targetHours` clears the target hours.
 */
export interface WagePeriodV3UpdateRequest {
  amount: number;
  wageType: WageType;
  targetHours: number | null;
}

/**
 * The body to reactivate an employee via the v3 employee API. `contractExpiryDate` must be a full date
 * (`YYYY-MM-DD`).
 */
export interface EmployeeReactivateV3Request {
  wage: WagePeriodV3Request;
  contractExpiryDate: string | null;
}

/**
 * One employment period as the v3 employee API returns it. Unlike the v1 `Employment`, its `id` is a uuid.
 */
export interface EmploymentPeriodV3Dto {
  id: string;
  employmentType: EmploymentType;
  validFrom: string;
  validTo: string | null;
}

/**
 * The body to create an employment period via the v3 employee API. `validFrom` must be a full date (`YYYY-MM-DD`).
 */
export interface EmploymentPeriodV3Request {
  employmentType: EmploymentType;
  validFrom: string;
}

/**
 * The body to update an employment period via the v3 employee API. Only the employment type can be changed.
 */
export interface EmploymentPeriodV3UpdateRequest {
  employmentType: EmploymentType;
}

/**
 * The per diems of an employee on one day as the v3 employee API returns them. Unlike the v1/v2 per diem, its
 * `id` is a uuid and the order is referenced by its uuid and name only.
 */
export interface EmployeePerDiemV3Dto {
  id: string;
  date: string;
  order: { id: string; name: string };
  catering: number;
  misc: number;
  breakfast: boolean;
  cateringAfterBreakfast: number;
}

/**
 * The per diems to record for an employee on one day via the v3 employee API. The order is referenced by its uuid
 * and must be one the employee logged time on that day. Both per diems zero and no breakfast is rejected; a per
 * diem is removed with {@link deletePerDiemV3} instead.
 */
export interface EmployeePerDiemV3UpdateRequest {
  orderId: string;
  catering: number;
  misc: number;
  breakfast: boolean;
}

/**
 * A new per diem for an employee via the v3 employee API: an {@link EmployeePerDiemV3UpdateRequest} plus its day.
 */
export interface EmployeePerDiemV3Request extends EmployeePerDiemV3UpdateRequest {
  date: string;
}

/**
 * The minutes per worklog type, summed over a day or a month, as the v3 employee API returns them.
 */
export interface EmployeeWorklogTotalsV3Dto {
  work: number;
  pause: number;
  travel: number;
  passenger: number;
}

/**
 * One worklog as the v3 employee API returns it. Unlike the v1/v2 worklog, its `id` is a uuid, `start` and `end`
 * are `HH:mm`, the type is called `type` and the order is referenced by its uuid and name only. A worklog whose
 * `start` equals its `end` is still open.
 */
export interface EmployeeWorklogV3Dto {
  id: string;
  start: string;
  end: string;
  minutes: number;
  type: WorklogType;
  order: { id: string; name: string };
  comment?: string;
}

/**
 * The worklogs of an employee on one day, ordered by start time, with their sums per worklog type.
 */
export interface EmployeeWorklogDayV3Dto {
  date: string;
  worklogs: Array<EmployeeWorklogV3Dto>;
  totals: EmployeeWorklogTotalsV3Dto;
}

/**
 * The worklogs of an employee in one month, grouped by day. `days` only lists days with worklogs, oldest first.
 */
export interface EmployeeWorklogsV3Response {
  month: string;
  days: Array<EmployeeWorklogDayV3Dto>;
  totals: EmployeeWorklogTotalsV3Dto;
}

/**
 * The time an employee logged in one month, with the employment and wage valid on its first day. `employment`
 * and `wage` are `null` when none is valid on that day.
 */
export interface EmployeeMonthlySummaryV3Dto {
  employee: EmployeeV3Dto;
  minutes: EmployeeWorklogTotalsV3Dto;
  employment: EmploymentPeriodV3Dto | null;
  wage: WagePeriodV3Dto | null;
}

/**
 * Maps a v3 employee DTO onto an {@link EmployeeName} so callers do not need to know the v3 field names.
 * The numeric database `id` is not exposed by v3, so it is left undefined; use `uuid` to identify the
 * employee for further v3 calls, and `employeeId` for everything else (routing, v1/v2 calls, ...).
 */
function toEmployeeName(dto: EmployeeV3Dto): EmployeeName {
  const employeeName = new EmployeeName();
  employeeName.uuid = dto.id;
  employeeName.employeeId = dto.employeeNumber;
  employeeName.firstName = dto.firstName;
  employeeName.lastName = dto.lastName;
  return employeeName;
}

/**
 * Maps a v3 worklog onto a {@link Worklog} so the accounting views and the worklog dialog can keep their v1/v2 field
 * names. The numeric ids of the worklog and its order are not exposed by v3, so they are left undefined; use `uuid`
 * to identify both for further v3 calls.
 */
function toWorklog(dto: EmployeeWorklogV3Dto, date: string): Worklog {
  return Object.assign(new Worklog(), {
    uuid: dto.id,
    date: date,
    start: dto.start,
    end: dto.end,
    worklogType: dto.type,
    comment: dto.comment ?? "",
    order: Object.assign(new Order(), {uuid: dto.order.id, name: dto.order.name}),
    employee: null
  });
}

/**
 * One row of the employee table/list, flattened from a `FULL` v3 employee DTO into the field names the
 * v1/v2 APIs used. `wage` and `wageType` are null when the caller may not see the employee's wage.
 */
export interface EmployeeListItem {
  uuid: string;
  employeeId: number;
  firstName: string;
  lastName: string;
  active: boolean;
  wage: number | null;
  wageType: WageType | null;
  employmentType: EmploymentType | null;
  contractExpiryDate: string | null;
  operatorNumber: number | null;
}

function toEmployeeListItem(dto: EmployeeV3Dto): EmployeeListItem {
  return {
    uuid: dto.id,
    employeeId: dto.employeeNumber,
    firstName: dto.firstName,
    lastName: dto.lastName,
    active: dto.status === EmployeeStatus.ACTIVE,
    wage: dto.currentWage?.amount ?? null,
    wageType: dto.currentWage?.wageType ?? null,
    employmentType: dto.currentEmployment?.employmentType ?? null,
    contractExpiryDate: dto.contractExpiryDate ?? null,
    operatorNumber: dto.operatorNumber ?? null
  };
}

/**
 * Maps a `FULL` v3 employee DTO onto an {@link Employee} so the employee details cards can keep their v1 field names.
 * The numeric database `id` is not exposed by v3, so it is left undefined; use `uuid` to identify the employee. The
 * wage and employment periods are left empty, as the cards load them on their own.
 */
export function toEmployee(dto: EmployeeV3Dto): Employee {
  return Object.assign(new Employee(), {
    uuid: dto.id,
    employeeId: dto.employeeNumber,
    firstName: dto.firstName,
    lastName: dto.lastName,
    birthdate: dto.birthdate,
    email: dto.email,
    companyLocation: dto.companyLocation ? {uuid: dto.companyLocation.id, name: dto.companyLocation.name} : null,
    firstAidCourseDate: dto.firstAidCourseDate,
    firstAidValidUntil: dto.firstAidValidUntil,
    contractExpiryDate: dto.contractExpiryDate,
    export: dto.export,
    temporary: dto.temporary,
    manager: dto.manager,
    teamleadSince: dto.teamleadSince,
    operatorNumber: dto.operatorNumber,
    inventoryBonusEligible: dto.inventoryBonusEligible,
    isInventoryBonusEligibleSince: dto.inventoryBonusEligibleSince,
    active: dto.status === EmployeeStatus.ACTIVE
  });
}

/**
 * Builds the {@link EmployeeV3Patch} for the master data the employee details cards edit: the personal data and the
 * export flag. `temporary` and the inventory bonus eligibility are changed on their own and left out. Text fields
 * hold numbers as strings and cleared fields as `""`, so numbers are parsed and blank values are sent as `null`.
 */
export function toEmployeeV3Patch(employee: Employee): EmployeeV3Patch {
  return {
    employeeNumber: toNumberOrNull(employee.employeeId) as number,
    firstName: employee.firstName as string,
    lastName: employee.lastName as string,
    birthdate: employee.birthdate as string,
    email: employee.email as string,
    companyLocationId: employee.companyLocation?.uuid ?? null,
    firstAidCourseDate: employee.firstAidCourseDate || null,
    firstAidValidUntil: employee.firstAidValidUntil || null,
    contractExpiryDate: employee.contractExpiryDate || null,
    export: employee.export,
    manager: employee.manager,
    teamleadSince: employee.teamleadSince || null,
    operatorNumber: toNumberOrNull(employee.operatorNumber)
  };
}

function toNumberOrNull(value: number | string | null | undefined): number | null {
  return value === null || value === undefined || value === "" ? null : Number(value);
}

/**
 * A welcome document as the v3 employee API returns it: the PDF and the name to download it as.
 */
export interface WelcomeDocument {
  filename: string;
  content: Blob;
}

export default {
  /**
   * Finds employees via the v3 API. `sort` accepts employeeNumber, firstName, lastName, wage,
   * contractExpiryDate and operatorNumber; sorting by wage is forbidden for callers who may not see wages.
   */
  findEmployeesV3(filter: EmployeeV3Filter | null, projection: EmployeeProjection, size: number | null, page: number | null, sort: string | null): Promise<AxiosResponse<SpringPagedResult<EmployeeV3Dto>>> {
    return axios.get(`${PATH_V3}`, {
      params: {
        term: filter?.term,
        status: filter?.status,
        operatorNumber: filter?.operatorNumber,
        employeeNumber: filter?.employeeNumber,
        firstName: filter?.firstName,
        lastName: filter?.lastName,
        employmentType: filter?.employmentType,
        wage: filter?.wage,
        wageType: filter?.wageType,
        projection: projection,
        size: size ? size : 250,
        page: page ? page : 0,
        sort: sort ? sort : null
      }
    });
  },
  /**
   * Finds employees via the v3 API for the employee table/list, already mapped to {@link EmployeeListItem}s.
   */
  findEmployeeListItemsV3(filter: EmployeeV3Filter | null, size: number | null, page: number | null, sort: string | null): Promise<SpringPagedResult<EmployeeListItem>> {
    return this.findEmployeesV3(filter, "FULL", size, page, sort)
      .then(response => ({
        content: response.data.content.map(toEmployeeListItem),
        page: response.data.page
      }));
  },
  /**
   * Finds employees by a search term via the v3 API, returning already-mapped {@link EmployeeName}s. Uses the
   * `SHALLOW` projection, which only returns the id, employeeNumber, firstName and lastName.
   */
  findByTermV3(term: string): Promise<Array<EmployeeName>> {
    return this.findEmployeesV3({term: term}, "SHALLOW", 10, 0, null)
      .then(response => response.data.content.map(toEmployeeName));
  },
  /**
   * Gets one employee via the v3 API by its uuid (not its numeric employeeId - resolve the uuid first, e.g.
   * from a v1/v2 response's `uuid` field, or from {@link findEmployeesV3}/{@link findByTermV3}).
   */
  getEmployeeV3(uuid: string, projection: EmployeeProjection): Promise<AxiosResponse<EmployeeV3Dto>> {
    return axios.get(`${PATH_V3}/${uuid}`, {
      params: {
        projection: projection
      }
    });
  },
  /**
   * Gets all wage periods of an employee via the v3 API by its uuid, newest first by start date. The list is
   * empty when the caller may not see the employee's wages.
   */
  getEmployeeWagesV3(uuid: string): Promise<Array<WagePeriodV3Dto>> {
    return axios.get(`${PATH_V3}/${uuid}/wages`)
      .then(response => response.data.wages);
  },
  /**
   * Starts a new wage period via the v3 API. The wage period covering the day before ends that day.
   */
  createWageV3(uuid: string, wage: WagePeriodV3Request): Promise<WagePeriodV3Dto> {
    return axios.post(`${PATH_V3}/${uuid}/wages`, wage)
      .then(response => response.data);
  },
  /**
   * Replaces the amount, wage type and target hours of a wage period via the v3 API.
   */
  updateWageV3(uuid: string, wageId: string, wage: WagePeriodV3UpdateRequest): Promise<WagePeriodV3Dto> {
    return axios.put(`${PATH_V3}/${uuid}/wages/${wageId}`, wage)
      .then(response => response.data);
  },
  /**
   * Deletes a wage period via the v3 API. The wage period covering the day before takes over its end date.
   */
  deleteWageV3(uuid: string, wageId: string) {
    return axios.delete(`${PATH_V3}/${uuid}/wages/${wageId}`);
  },
  /**
   * Gets all employment periods of an employee via the v3 API by its uuid, newest first by start date.
   */
  getEmployeeEmploymentsV3(uuid: string): Promise<Array<EmploymentPeriodV3Dto>> {
    return axios.get(`${PATH_V3}/${uuid}/employments`)
      .then(response => response.data.employments);
  },
  /**
   * Starts a new employment period via the v3 API. The employment period covering the day before ends that day.
   */
  createEmploymentV3(uuid: string, employment: EmploymentPeriodV3Request): Promise<EmploymentPeriodV3Dto> {
    return axios.post(`${PATH_V3}/${uuid}/employments`, employment)
      .then(response => response.data);
  },
  /**
   * Replaces the employment type of an employment period via the v3 API.
   */
  updateEmploymentV3(uuid: string, employmentId: string, employment: EmploymentPeriodV3UpdateRequest): Promise<EmploymentPeriodV3Dto> {
    return axios.put(`${PATH_V3}/${uuid}/employments/${employmentId}`, employment)
      .then(response => response.data);
  },
  /**
   * Deletes an employment period via the v3 API. The employment period covering the day before takes over its end
   * date.
   */
  deleteEmploymentV3(uuid: string, employmentId: string) {
    return axios.delete(`${PATH_V3}/${uuid}/employments/${employmentId}`);
  },
  getDefaultData(registrationUuid?: string): Promise<AxiosResponse<Employee>> {
    return axios.get(`${PATH}/default`, {
      params: {
        registrationUuid: registrationUuid
      }
    });
  },
  createEmployee(employee: Employee, registrationUuid?: string): Promise<AxiosResponse<Employee>> {
    return axios.post(`${PATH}`, {
      // Left empty, the backend generates the employee number
      employeeId: toNumberOrNull(employee.employeeId),
      firstName: employee.firstName,
      lastName: employee.lastName,
      birthdate: employee.birthdate,
      email: employee.email,
      companyLocation: employee.companyLocation,
      contractExpiryDate: employee.contractExpiryDate,
      export: employee.export,
      employment: employee.employments[0],
      wage: employee.wages[0],
      operatorNumber: employee.operatorNumber,
      inventoryBonusEligible: employee.inventoryBonusEligible,

      registrationUuid: registrationUuid
    }, {
      headers: {
        "Content-Type": "application/json"
      }
    });
  },
  /**
   * Creates the welcome document of an employee via the v3 API by its uuid. Sets a new initial password, so any
   * welcome document created before stops working. Answers 409 if the employee has already logged in and the caller
   * is not a Superadmin.
   */
  createWelcomePdf(uuid: string): Promise<WelcomeDocument> {
    return axios.post(`${PATH_V3}/${uuid}/welcome-document`, null, {
      responseType: "arraybuffer",
      transformResponse: parseProblemBody
    })
      .then(response => ({
        filename: toFilename(response.headers["content-disposition"], "welcome.pdf"),
        content: new Blob([response.data], {type: "application/pdf"})
      }));
  },
  /**
   * Changes an employee's master data via the v3 API by its uuid with a JSON Merge Patch and returns the changed
   * employee in its `FULL` form. Answers 409 if the employee number or the operator number belongs to another
   * employee, and 422 with the rejected fields under `errors` otherwise.
   */
  updateEmployeeV3(uuid: string, patch: EmployeeV3Patch): Promise<EmployeeV3Dto> {
    return axios.patch(`${PATH_V3}/${uuid}`, patch, {
      headers: {
        "Content-Type": "application/merge-patch+json"
      }
    })
      .then(response => response.data);
  },
  /**
   * Deactivates an employee via the v3 API by its uuid. Answers 204 on success and 409 if the employee is
   * already inactive.
   */
  deactivateEmployee(uuid: string) {
    return axios.post(`${PATH_V3}/${uuid}/deactivate`);
  },
  /**
   * Reactivates an employee via the v3 API by its uuid and starts the given wage period, ending the previous open
   * one the day before. Answers 409 if the employee is already active.
   */
  reactivateEmployee(uuid: string, request: EmployeeReactivateV3Request): Promise<EmployeeV3Dto> {
    return axios.post(`${PATH_V3}/${uuid}/reactivate`, request)
      .then(response => response.data);
  },
  /**
   * Gets an employee's worklogs of a month via the v3 API by its uuid, grouped by day. Only the employee
   * themselves and a Superadmin may see them.
   */
  getEmployeeWorklogsV3(uuid: string, yearMonth: string): Promise<EmployeeWorklogsV3Response> {
    return axios.get(`${PATH_V3}/${uuid}/worklogs`, {
      params: {
        month: yearMonth
      }
    })
      .then(response => response.data);
  },
  /**
   * Gets an employee's worklogs of a month via the v3 API by its uuid, flattened into {@link Worklog}s ordered by day
   * and start time.
   */
  getEmployeeWorklogListV3(uuid: string, yearMonth: string): Promise<Array<Worklog>> {
    return this.getEmployeeWorklogsV3(uuid, yearMonth)
      .then(worklogs => worklogs.days
        .flatMap(day => day.worklogs.map(worklog => toWorklog(worklog, day.date))));
  },
  /**
   * Gets a summary of every employee who logged time in a month via the v3 API, ordered by employee number. A
   * caller who may view only themselves gets at most their own summary.
   */
  getEmployeeMonthlySummariesV3(yearMonth: string): Promise<Array<EmployeeMonthlySummaryV3Dto>> {
    return axios.get(PATH_MONTHLY_SUMMARIES_V3, {
      params: {
        month: yearMonth
      }
    })
      .then(response => response.data.summaries);
  },
  /**
   * Gets an employee's per diems of a month via the v3 API by its uuid, oldest first.
   */
  getEmployeePerDiemsV3(uuid: string, yearMonth: string): Promise<Array<EmployeePerDiemV3Dto>> {
    return axios.get(`${PATH_V3}/${uuid}/per-diems`, {
      params: {
        month: yearMonth
      }
    })
      .then(response => response.data.perDiems);
  },
  /**
   * Records a per diem for an employee via the v3 API. The employee must not have one on that day yet.
   */
  createPerDiemV3(uuid: string, perDiem: EmployeePerDiemV3Request): Promise<EmployeePerDiemV3Dto> {
    return axios.post(`${PATH_V3}/${uuid}/per-diems`, perDiem)
      .then(response => response.data);
  },
  /**
   * Replaces the order, amounts and breakfast of an employee's per diem via the v3 API. Its day stays as it is.
   */
  updatePerDiemV3(uuid: string, perDiemId: string, perDiem: EmployeePerDiemV3UpdateRequest): Promise<EmployeePerDiemV3Dto> {
    return axios.put(`${PATH_V3}/${uuid}/per-diems/${perDiemId}`, perDiem)
      .then(response => response.data);
  },
  /**
   * Deletes an employee's per diem via the v3 API.
   */
  deletePerDiemV3(uuid: string, perDiemId: string): Promise<AxiosResponse<void>> {
    return axios.delete(`${PATH_V3}/${uuid}/per-diems/${perDiemId}`);
  }
};
